import { useState, useEffect, useCallback } from 'react';
import { apiCall } from '../services/api';
import { useUserId } from '../context/UserAuthContext';

const API_URL = typeof window !== 'undefined' ? window.location.origin : '';

/**
 * Custom hook for managing Nylas-connected accounts.
 * Hidden-account state is persisted in MongoDB so it survives redeployments.
 * localStorage is used only as a fast synchronous cache.
 */
const useNylasAccounts = (userIdOverride = null, accountType = 'email') => {
  const authUserId = useUserId();
  const userId = userIdOverride || authUserId;

  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deletedTabs, setDeletedTabs] = useState(() => {
    // Bootstrap from localStorage for instant first render; DB load overwrites this
    try {
      const saved = localStorage.getItem(`deleted_${accountType}_tabs`);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [hiddenLoaded, setHiddenLoaded] = useState(false);

  // ── Load hidden accounts from DB on mount ──────────────────────────────────
  useEffect(() => {
    if (!userId) return;
    fetch(`${API_URL}/api/nylas/hidden-accounts?user_id=${userId}&context=${accountType}`)
      .then(r => r.ok ? r.json() : { hidden: [] })
      .then(data => {
        const hidden = data.hidden || [];
        setDeletedTabs(hidden);
        // Keep localStorage in sync
        localStorage.setItem(`deleted_${accountType}_tabs`, JSON.stringify(hidden));
      })
      .catch(() => { /* keep localStorage fallback already in state */ })
      .finally(() => setHiddenLoaded(true));
  }, [userId, accountType]);

  // ── Persist hidden list to DB + localStorage whenever it changes ───────────
  const persistHidden = useCallback(async (list) => {
    localStorage.setItem(`deleted_${accountType}_tabs`, JSON.stringify(list));
    if (!userId) return;
    try {
      await fetch(`${API_URL}/api/nylas/hidden-accounts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, context: accountType, identifiers: list }),
      });
    } catch (e) {
      console.error('Failed to persist hidden accounts:', e);
    }
  }, [userId, accountType]);

  // ── Fetch accounts from Nylas API ─────────────────────────────────────────
  const fetchAccounts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiCall(`/nylas/accounts?user_id=${userId}&type=${accountType}`);
      if (data && Array.isArray(data.accounts)) {
        const filtered = data.accounts.filter(
          account => !deletedTabs.includes(account.grant_id || account.email)
        );
        setAccounts(filtered);
      } else {
        setAccounts([]);
      }
    } catch (err) {
      console.error(`Failed to fetch ${accountType} accounts:`, err);
      setError(err.message || `Failed to load ${accountType} accounts`);
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  }, [userId, accountType, deletedTabs]);

  // Re-fetch when hidden list is loaded from DB
  useEffect(() => {
    if (hiddenLoaded) fetchAccounts();
  }, [hiddenLoaded]);

  // Load on mount (before DB load, so we at least show cached state)
  useEffect(() => {
    fetchAccounts();
  }, []);

  // ── Connect a new account via Nylas OAuth ─────────────────────────────────
  const connectAccount = useCallback(async (scopes = ['email']) => {
    try {
      const redirectHost = window.location.origin;
      // Pass Accept: application/json so backend returns { auth_url } instead of a redirect
      const response = await apiCall(
        `/nylas/auth/connect?user_id=${userId}&scopes=${scopes.join(',')}&redirect_host=${encodeURIComponent(redirectHost)}`,
        { headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' } }
      );
      if (response?.auth_url) {
        // Navigate browser to Nylas OAuth page
        window.location.href = response.auth_url;
      } else {
        setError('Could not start email connection. Please try again.');
      }
    } catch (err) {
      console.error('Failed to initiate Nylas connection:', err);
      setError(err.message || 'Failed to connect account');
    }
  }, [userId]);

  // ── Disconnect an account (calls Nylas API + hides) ───────────────────────
  const disconnectAccount = useCallback(async (grantId, email) => {
    try {
      await apiCall(`/nylas/disconnect?grant_id=${grantId}`, { method: 'DELETE' });
      const identifier = grantId || email;
      const next = [...deletedTabs, identifier];
      setDeletedTabs(next);
      await persistHidden(next);
      setAccounts(prev => prev.filter(a => (a.grant_id || a.email) !== identifier));
      return true;
    } catch (err) {
      console.error('Failed to disconnect account:', err);
      setError(err.message || 'Failed to disconnect account');
      return false;
    }
  }, [deletedTabs, persistHidden]);

  // ── Hide an account (page-local delete, no Nylas API call) ────────────────
  const hideAccount = useCallback(async (identifier) => {
    const next = [...deletedTabs, identifier];
    setDeletedTabs(next);
    await persistHidden(next);
    setAccounts(prev => prev.filter(a => (a.grant_id || a.email) !== identifier));
  }, [deletedTabs, persistHidden]);

  // ── Restore a hidden account ───────────────────────────────────────────────
  const restoreAccount = useCallback(async (identifier) => {
    const next = deletedTabs.filter(id => id !== identifier);
    setDeletedTabs(next);
    await persistHidden(next);
    fetchAccounts();
  }, [deletedTabs, persistHidden, fetchAccounts]);

  // ── Clear all hidden accounts ─────────────────────────────────────────────
  const clearDeletedTabs = useCallback(async () => {
    setDeletedTabs([]);
    await persistHidden([]);
    fetchAccounts();
  }, [persistHidden, fetchAccounts]);

  return {
    accounts,
    loading,
    error,
    fetchAccounts,
    connectAccount,
    disconnectAccount,
    hideAccount,
    restoreAccount,
    clearDeletedTabs,
    deletedTabs,
  };
};

export default useNylasAccounts;
